import { Dialog } from '@/components/Dialog';
import { Callout } from '@/components/Elements';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useSavedFiltersStatus } from '@/features/analyze/hooks/useSavedFiltersStatus';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateFilterMode } from './CreateFilterMode';
import { OverwriteFilterMode } from './OverwriteFilterMode';

type ModalMode = 'create' | 'overwrite';

interface SaveFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SaveFilterModal = ({ isOpen, onClose }: SaveFilterModalProps) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ModalMode>('create');
  const { isFull: isSavedFiltersFull, count: savedFiltersCount } =
    useSavedFiltersStatus();

  useEffect(() => {
    // メニューが開いたときにモードを決定する
    if (!isOpen) return;

    setMode(isSavedFiltersFull ? 'overwrite' : 'create');
  }, [isOpen, isSavedFiltersFull]);

  const handleModeSelect = (newMode: ModalMode) => {
    setMode(newMode);
  };

  const handleSuccess = () => {
    onClose();
  };

  const renderContent = () => {
    switch (mode) {
      case 'create':
        return <CreateFilterMode onClose={onClose} onSuccess={handleSuccess} />;
      case 'overwrite':
        return (
          <OverwriteFilterMode onClose={onClose} onSuccess={handleSuccess} />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('フィルタを保存')}
      maxWidth="lg"
    >
      <div className="max-h-[60vh] space-y-6 overflow-y-scroll">
        {/* 上限到達時の警告メッセージ */}
        {isSavedFiltersFull && (
          <Callout variant="warning">
            {t(
              '保存可能なフィルター数が上限に達しています。既存のフィルターを上書きするか、不要なフィルターを削除してから新規作成してください。'
            )}
          </Callout>
        )}

        {/* ToggleSwitch（上限に達していない場合かつ保存済みフィルターが存在する場合のみ表示） */}
        {!isSavedFiltersFull && savedFiltersCount > 0 && (
          <ToggleSwitch
            checked={mode === 'overwrite'}
            onToggle={(checked) => {
              handleModeSelect(checked ? 'overwrite' : 'create');
            }}
            labels={[t('新規作成'), t('上書き')]}
          />
        )}

        {renderContent()}
      </div>
    </Dialog>
  );
};
